<template>
    <div v-auto-animate class="mt-8">
        <div class="mt-4">
            <Label class="mb-2">{{ $t("teams.round_robin.type") }}</Label>
            <Select
                v-model="selectedRoundRobin"
                @update:model-value="onRoundRobinPriorityTypeChange"
            >
                <SelectTrigger>
                    <SelectValue
                        placeholder="Vyberte type rozděleování rezervací"
                    />
                </SelectTrigger>
                <SelectContent>
                    <SelectGroup>
                        <SelectItem value="priority">
                            {{ $t("teams.round_robin.priority") }}
                        </SelectItem>
                        <SelectItem value="equal_distribution">
                            {{ $t("teams.round_robin.equal") }}
                        </SelectItem>
                    </SelectGroup>
                </SelectContent>
            </Select>
        </div>

        <div class="mt-4" v-if="selectedRoundRobin === 'priority'">
            <Label class="mb-1"
                >{{ $t("teams.round_robin.user_order") }}
            </Label>

            <PerfectScrollbar class="max-h-[500px]">
                <VueDraggable
                    ref="el"
                    v-model="list"
                    :animation="150"
                    target=".sort-target"
                    handle=".event-card-handle"
                    ghost-class="ghost"
                    @end="onEnd"
                    @start="onStart"
                >
                    <TransitionGroup
                        type="transition"
                        tag="ul"
                        :name="!drag ? 'fade' : undefined"
                        class="sort-target"
                    >
                        <li
                            class="mt-2 flex gap-2 bg-stone-50 px-4 py-2"
                            v-for="item in list"
                            :key="item.id"
                        >
                            <Grip class="event-card-handle w-4 cursor-move" />

                            {{ item.name }}
                        </li>
                    </TransitionGroup>
                </VueDraggable>
            </PerfectScrollbar>
        </div>
    </div>
</template>

<script setup lang="ts">
import { VueDraggable } from "vue-draggable-plus";
import { ref, nextTick, watch } from "vue";
import route from "ziggy-js";
import { SortableEvent } from "sortablejs";
import { router } from "@inertiajs/vue3";
import { PerfectScrollbar } from "vue3-perfect-scrollbar";
import { EventType, RoundRobinPrioirty, User } from "../types/EventType";
import { Grip } from "lucide-vue-next";
import { UserWithAssignement } from "../types/UserWithAssignment";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const props = defineProps<{
    users: UserWithAssignement[];
    eventType: EventType;
}>();

const drag = ref(false);

const list = ref<User[]>([]);

const selectedRoundRobin = ref(
    props.eventType.roundRobinType ?? "equal_distribution",
);

watch(
    () => props.eventType.users,
    () => {
        list.value = getInitiallyPriorityOrderedUsers(
            props.users,
            props.eventType.roundRobinSettings.priority ?? [],
        );
    },
);

watch(selectedRoundRobin, (val) => {
    if (val === "priority") {
        getInitiallyPriorityOrderedUsers(
            props.users,
            props.eventType.roundRobinSettings.priority ?? [],
        );
    }
});

list.value = getInitiallyPriorityOrderedUsers(
    props.users,
    props.eventType.roundRobinSettings.priority ?? [],
);

function onRoundRobinPriorityTypeChange(type: string) {
    router.post(
        route("app.event-types.round-robin-settings.update", {
            eventType: props.eventType.id,
        }),
        {
            type: type,
            priority:
                type === "priority" ? getPriorityArray(list.value) : undefined,
        },
    );
}
function getInitiallyPriorityOrderedUsers(
    users: User[],
    priority: RoundRobinPrioirty[],
) {
    const sortedUsers: User[] = [];

    if (priority.length === 0) {
        return users;
    }

    const sortedPriorityArray = priority.toSorted(
        (a, b) => a.priority - b.priority,
    );

    for (const prio of sortedPriorityArray) {
        const foundUser = users.find((user) => user.id === prio.user_id);

        if (foundUser) {
            sortedUsers.push(foundUser);
        }
    }

    return sortedUsers;
}

function onEnd(_: SortableEvent) {
    nextTick(() => {
        drag.value = false;
    });
    reorder();
}

function getPriorityArray(list: User[]): RoundRobinPrioirty[] {
    return list.map((item, index) => {
        return {
            user_id: item.id,
            priority: index,
        };
    });
}

async function reorder() {
    onRoundRobinPriorityTypeChange(selectedRoundRobin.value);
}

function onStart() {
    drag.value = true;
}
</script>

<style>
.fade-move,
.fade-enter-active,
.fade-leave-active {
    transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: scaleY(0.01) translate(30px, 0);
}

.fade-leave-active {
    position: absolute;
}

.ghost {
    opacity: 0.5;
    background: #c8ebfb;
}
</style>
